import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Subject, catchError, startWith, switchMap, tap } from 'rxjs';
import { escapeHtml } from '../../utils/html';
import { HasPermissionDirective, isHttpErrorResponse, PermissionService } from '@ipg/ngx-frontend-toolset';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { DialogService } from '../../components/dialog/dialog.service';
import { UserRole } from '../../models/user-role.model';
import { UserRoleService } from '../../services/user-role.service';
import { UnitService } from '../units/services/unit.service';

@Component({
  selector: 'app-settings-roles',
  imports: [
    MatButton,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTableModule,
    DatePipe,
    DataTableContainerComponent,
    MatFormField,
    FormsModule,
    MatInput,
    MatSort,
    MatSortHeader,
    MatSuffix,
    MatTooltip,
    HasPermissionDirective,
  ],
  templateUrl: './settings-roles.component.html',
  styleUrl: './settings-roles.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsRolesComponent {
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _roleService = inject(UserRoleService);
  private readonly _dialogService = inject(DialogService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _unitService = inject(UnitService);

  protected readonly isRootUnit = computed(
    () => this._unitService.currentUnit()?.prevUnitId === null
  );
  private readonly _fetch$ = new Subject<void>();

  protected readonly keyword = signal<string | null>(null);

  private readonly _sort = signal({
    sortBy: 'label',
    sortOrder: 'asc',
  });

  private readonly _roles$ = this._fetch$.pipe(
    startWith(undefined),
    switchMap(() =>
      this._roleService.getList({
        params: {
          sortBy: this._sort().sortBy,
          sortOrder: this._sort().sortOrder,
        },
      })
    )
  );

  private readonly _allRoles = toSignal(this._roles$, { initialValue: [] });

  /** Client-side keyword filtering */
  protected readonly dataSource = computed(() => {
    const roles = this._allRoles();
    const kw = this.keyword()?.toLowerCase();
    if (!kw) {
      return roles;
    }
    return roles.filter((role) => role.label.toLowerCase().includes(kw));
  });

  protected readonly hasNoData = computed(() => this.dataSource().length === 0);

  displayedColumns = [
    'label',
    'description',
    'permissionCount',
    'createdAt',
    'actions',
  ];

  protected handleOpenMatrix(): void {
    this._router.navigate(['matrix'], { relativeTo: this._route });
  }

  protected handleCreateRole(): void {
    this._router.navigate(['new'], { relativeTo: this._route });
  }

  protected handleEditRole(role: UserRole): void {
    if (!this._permissionService.hasPermission('user_role:update')) return;
    this._router.navigate([role._id], { relativeTo: this._route });
  }

  protected handleDeleteRole(role: UserRole): void {
    const dialogRef = this._dialogService.confirm({
      title: 'Rol verwijderen',
      message: `Weet je zeker dat je de rol <strong>${escapeHtml(role.label)}</strong> wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`,
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      onConfirm: () =>
        this._roleService.delete(role._id).pipe(
          catchError((error) => {
            dialogRef.close();

            if (
              isHttpErrorResponse(error) &&
              error.status === 409 &&
              error.error?.detail?.errorCode === 'USER_ROLE_IN_USE'
            ) {
              this._dialogService.alert({
                title: 'Rol in gebruik',
                message: `De rol <strong>${escapeHtml(role.label)}</strong> kan niet verwijderd worden omdat er nog gebruikers aan gekoppeld zijn. Wijs deze gebruikers eerst een andere rol toe.`,
                confirmText: 'OK',
              });
            } else {
              this._dialogService.alert({
                title: 'Foutmelding',
                message: `Er is iets misgegaan bij het verwijderen van rol ${escapeHtml(role.label)}. Probeer het later nog eens.`,
                confirmText: 'OK',
              });
            }

            return EMPTY;
          }),
          tap(() => this._fetch$.next())
        ),
    });
  }

  handleSortChange({ active, direction }: Sort) {
    this._sort.set({
      sortBy: active,
      sortOrder: direction || 'asc',
    });
    this._fetch$.next();
  }

  handleSearch(value: string) {
    this.keyword.set(value.trim() || null);
  }

  clearSearch() {
    this.keyword.set(null);
  }
}
